import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Switch,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
  createGroup,
  deleteGroup,
  listGroups,
  updateGroup,
} from '../../../api/mailingApi';
import { useSelector } from 'react-redux';
import { getUser } from '../../../redux/usersRedux';

const warehouseOptions = [
  {
    value: '8AAA',
    label: '8AAA',
  },
  {
    value: '8AAB',
    label: '8AAB',
  },
  {
    value: '8AAC',
    label: '8AAC',
  },
];

const mailMessageTypeGroups = [
  // {
  //   group: 'PZ04',
  //   options: [
  //     {
  //       value: 'PZ04_QUARANTINE',
  //       label: 'PZ04 – Kwarantanna',
  //     },
  //     {
  //       value: 'PZ04_SUSPECT',
  //       label: 'PZ04 – Podejrzenie niezgodności',
  //     },
  //     {
  //       value: 'PZ04_INCONFORMITY',
  //       label: 'PZ04 – Niezgodność',
  //     },
  //     {
  //       value: 'PZ04_REPORT_MICRO_STAGE_1',
  //       label: 'PZ04 – Raport mikrobiologia – etap 1',
  //     },
  //     {
  //       value: 'PZ04_REPORT_MICRO_STAGE_2',
  //       label: 'PZ04 – Raport mikrobiologia – etap 2',
  //     },
  //     {
  //       value: 'PZ04_REPORT_MICRO_STAGE_3',
  //       label: 'PZ04 – Raport mikrobiologia – etap 3',
  //     },
  //     {
  //       value: 'PZ04_REPORT_MICRO_STAGE_4',
  //       label: 'PZ04 – Raport mikrobiologia – etap 4',
  //     },
  //     {
  //       value: 'PZ04_REPORT_MICRO_STAGE_5',
  //       label: 'PZ04 – Raport mikrobiologia – etap 5',
  //     },
  //     {
  //       value: 'PZ04_REPORT_FIZ_STAGE_1',
  //       label: 'PZ04 – Raport fizykochemia – etap 1',
  //     },
  //     {
  //       value: 'PZ04_REPORT_FIZ_STAGE_2',
  //       label: 'PZ04 – Raport fizykochemia – etap 2',
  //     },
  //     {
  //       value: 'PZ04_REPORT_FIZ_STAGE_3',
  //       label: 'PZ04 – Raport fizykochemia – etap 3',
  //     },
  //   ],
  // },
  {
    group: 'Inwentaryzacja i magazyn',
    options: [
      {
        value: 'INVENTORY_SUMMARY',
        label: 'Podsumowanie inwentaryzacji',
      },
      {
        value: 'BASIC_WAREHOUSE',
        label: 'Komunikat podstawowy magazynu',
      },
    ],
  },
  {
    group: 'Przekazanie produkcji – zgodne',
    options: [1, 2, 3, 4, 5, 6, 7].map((line) => ({
      value: `PRODUCTION_HANDOVER_SUMMARY_LINE_${line}`,
      label: `Przekazanie produkcji – zgodne – linia ${line}`,
    })),
  },
  {
    group: 'Przekazanie produkcji – do wyjaśnienia',
    options: [1, 2, 3, 4, 5, 6, 7].map((line) => ({
      value: `PRODUCTION_HANDOVER_DISCREPANCY_LINE_${line}`,
      label: `Przekazanie produkcji – do wyjaśnienia – linia ${line}`,
    })),
  },
  {
    group: 'Przekazanie produkcji – zamknięte z niezgodnością',
    options: [1, 2, 3, 4, 5, 6, 7].map((line) => ({
      value: `PRODUCTION_HANDOVER_CLOSED_WITH_DISCREPANCY_LINE_${line}`,
      label: `Przekazanie produkcji – zamknięte z niezgodnością – linia ${line}`,
    })),
  },
  {
    group: 'Analiza dostaw – zakończona',
    options: [
      {
        value: 'MIX_DELIVERY_ANALYSIS_COMPLETED',
        label: 'Analiza dostaw – zakończona',
      },
      {
        value: 'MIX_DELIVERY_WAITING_FOR_PRODUCTS',
        label: 'Analiza dostaw – oczekuje na produkty',
      },
      {
        value: 'MIX_DELIVERY_CHANGES_DETECTED',
        label: 'Analiza dostaw – wykryto zmiany',
      },
    ],
  },
];

const getMailMessageTypeLabel = (messageType) => {
  if (!messageType) {
    return '-';
  }

  for (const group of mailMessageTypeGroups) {
    const option = group.options.find((item) => item.value === messageType);

    if (option) {
      return option.label;
    }
  }

  return messageType;
};

const GroupsPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => getUser(state));
  const role = user?.users?.role;
  const userWarehouse = user?.users?.warehouse; // <-- u Ciebie tak wygląda
  const [filters, setFilters] = useState({
    warehouseCode: '',
    messageType: '',
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    msg: '',
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    warehouseCode: '',
    messageType: '',
    name: '',
  });

  const showError = (e, fallback) => {
    const msg =
      e?.response?.data?.message || e?.message || fallback || 'Wystąpił błąd';
    setSnack({ open: true, severity: 'error', msg: String(msg) });
  };

  const showOk = (msg) => setSnack({ open: true, severity: 'success', msg });

  async function load() {
    setLoading(true);
    try {
      const params = {};

      const isAdmin = role === 'ADMIN';

      // Jeśli NIE admin -> wymuś warehouseCode z profilu usera
      if (!isAdmin) {
        if (!userWarehouse) {
          throw new Error('Brak przypisanego magazynu dla użytkownika');
        }
        params.warehouseCode = userWarehouse;
      } else {
        // Admin może filtrować normalnie
        if (filters.warehouseCode.trim())
          params.warehouseCode = filters.warehouseCode.trim();
      }

      if (filters.messageType.trim())
        params.messageType = filters.messageType.trim();

      const data = await listGroups(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      showError(e, 'Błąd pobierania grup');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate() {
    const payload = {
      warehouseCode: createForm.warehouseCode.trim(),
      messageType: createForm.messageType.trim(),
      name: createForm.name.trim() || undefined,
    };

    if (!payload.warehouseCode || !payload.messageType) {
      setSnack({
        open: true,
        severity: 'error',
        msg: 'warehouseCode i messageType są wymagane',
      });
      return;
    }

    setLoading(true);
    try {
      await createGroup(payload);
      setCreateOpen(false);
      setCreateForm({ warehouseCode: '', messageType: '', name: '' });
      await load();
      showOk('Utworzono grupę');
    } catch (e) {
      showError(e, 'Błąd tworzenia grupy');
    } finally {
      setLoading(false);
    }
  }

  async function onToggleActive(row) {
    setLoading(true);
    try {
      await updateGroup({ id: row.id, isActive: !row.isActive });
      await load();
      showOk('Zaktualizowano status');
    } catch (e) {
      showError(e, 'Błąd zmiany statusu');
    } finally {
      setLoading(false);
    }
  }

  async function onDelete(row) {
    const ok = window.confirm(
      `Usunąć grupę?\n\nWarehouse: ${
        row.warehouseCode
      }\nType: ${getMailMessageTypeLabel(row.messageType)}\nEnum: ${
        row.messageType
      }\nName: ${row.name || '-'}`,
    );

    if (!ok) return;

    setLoading(true);

    try {
      await deleteGroup(row.id);
      await load();
      showOk('Usunięto grupę');
    } catch (e) {
      showError(e, 'Błąd usuwania grupy');
    } finally {
      setLoading(false);
    }
  }

  const columns = useMemo(
    () => [
      { field: 'warehouseCode', headerName: 'Magazyn', width: 140 },
      //
      {
        field: 'messageType',
        headerName: 'Typ komunikatu',
        minWidth: 360,
        flex: 1.2,
        renderCell: (params) => {
          const enumValue = params.row.messageType;
          const label = getMailMessageTypeLabel(enumValue);
          const showEnum = label !== enumValue;

          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                minWidth: 0,
              }}
            >
              <Typography variant="body2" noWrap title={label}>
                {label}
              </Typography>

              {/* {showEnum && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={enumValue}
                >
                  {enumValue}
                </Typography>
              )} */}
            </Box>
          );
        },
      },
      { field: 'name', headerName: 'Nazwa', flex: 1, minWidth: 120 },
      {
        field: 'isActive',
        headerName: 'Aktywny',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Switch
            checked={Boolean(params.row.isActive)}
            onChange={() => onToggleActive(params.row)}
            disabled={loading}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Akcje',
        width: 260,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/mailing/groups/${params.row.id}`)}
              disabled={loading}
            >
              Otwórz
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onDelete(params.row)}
              disabled={loading}
              color="error"
            >
              Usuń
            </Button>
          </Stack>
        ),
      },
    ],
    [loading, navigate], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5">Mailing - Grupy</Typography>

        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems="center"
          >
            <TextField
              size="small"
              label="Magazyn (warehouseCode)"
              value={
                role === 'ADMIN' ? filters.warehouseCode : userWarehouse || ''
              }
              onChange={(e) =>
                setFilters((s) => ({ ...s, warehouseCode: e.target.value }))
              }
              disabled={role !== 'ADMIN'}
            />
            <TextField
              size="small"
              label="Typ (messageType)"
              value={filters.messageType}
              onChange={(e) =>
                setFilters((s) => ({ ...s, messageType: e.target.value }))
              }
              sx={{ minWidth: 240 }}
            />
            <Button variant="contained" onClick={load} disabled={loading}>
              Filtruj
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setFilters({ warehouseCode: '', messageType: '' });
                setTimeout(load, 0);
              }}
              disabled={loading}
            >
              Reset
            </Button>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="contained"
              onClick={() => setCreateOpen(true)}
              disabled={loading}
            >
              Dodaj grupę
            </Button>
          </Stack>

          {loading ? (
            <Alert
              sx={{ mt: 2 }}
              severity="info"
              icon={<CircularProgress size={16} />}
            >
              Ładowanie...
            </Alert>
          ) : null}
        </Paper>

        <Paper sx={{ height: 560, width: '100%', p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
          />
        </Paper>
      </Stack>

      {/* CREATE DIALOG */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Dodaj grupę mailingową</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Magazyn</InputLabel>

              <Select
                label="Magazyn"
                value={createForm.warehouseCode}
                onChange={(event) =>
                  setCreateForm((previous) => ({
                    ...previous,
                    warehouseCode: event.target.value,
                  }))
                }
              >
                {warehouseOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Typ komunikatu</InputLabel>

              <Select
                label="Typ komunikatu"
                value={createForm.messageType}
                onChange={(event) =>
                  setCreateForm((previous) => ({
                    ...previous,
                    messageType: event.target.value,
                  }))
                }
              >
                {mailMessageTypeGroups.map((group) => [
                  <ListSubheader key={group.group}>
                    {group.group}
                  </ListSubheader>,

                  ...group.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>

            <TextField
              label="Nazwa grupy"
              value={createForm.name}
              onChange={(event) =>
                setCreateForm((previous) => ({
                  ...previous,
                  name: event.target.value,
                }))
              }
              fullWidth
              helperText="Opcjonalnie, np. Przekazanie produkcji – zgodne – linia 1"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={loading}>
            Anuluj
          </Button>

          <Button
            variant="contained"
            onClick={onCreate}
            disabled={
              loading || !createForm.warehouseCode || !createForm.messageType
            }
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupsPage;
